import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, TrendingUp, Award, Plus, BarChart3, Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface QualityStandard {
  id: string;
  name: string;
  description: string;
  target_score: number;
  current_score: number;
  category: string;
  created_at: string;
}

interface QualityMetric {
  id: string;
  standard_id: string;
  period: string;
  score: number;
  notes: string;
  recorded_by: string;
  recorded_at: string;
}

export default function ServiceQualityPage() {
  const [isNewStandardOpen, setIsNewStandardOpen] = useState(false);
  const [isNewMetricOpen, setIsNewMetricOpen] = useState(false);
  const [selectedStandard, setSelectedStandard] = useState<string>("");
  const [newStandard, setNewStandard] = useState({
    name: "",
    description: "",
    target_score: 85,
    category: ""
  });
  const [newMetric, setNewMetric] = useState({
    score: 0,
    notes: "",
    period: new Date().toISOString().slice(0, 7) // YYYY-MM format
  });

  const queryClient = useQueryClient();

  const { data: standards = [] } = useQuery({
    queryKey: ['quality-standards'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quality_standards' as any)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as QualityStandard[]) || [];
    }
  });

  const { data: metrics = [] } = useQuery({
    queryKey: ['quality-metrics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quality_metrics' as any)
        .select('*')
        .order('recorded_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as QualityMetric[]) || [];
    }
  });

  const createStandardMutation = useMutation({
    mutationFn: async (standard: any) => {
      const { data, error } = await supabase
        .from('quality_standards' as any)
        .insert([{ ...standard, current_score: 0 }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality-standards'] });
      setIsNewStandardOpen(false);
      setNewStandard({ name: "", description: "", target_score: 85, category: "" });
      toast.success("تم إنشاء معيار الجودة بنجاح");
    }
  });

  const createMetricMutation = useMutation({
    mutationFn: async (metric: any) => {
      const { data, error } = await supabase
        .from('quality_metrics' as any)
        .insert([{
          ...metric,
          standard_id: selectedStandard,
          recorded_at: new Date().toISOString()
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality-metrics'] });
      setIsNewMetricOpen(false);
      setNewMetric({ score: 0, notes: "", period: new Date().toISOString().slice(0, 7) });
      toast.success("تم تسجيل القياس بنجاح");
    }
  });

  const getScoreColor = (current: number, target: number) => {
    const percentage = (current / target) * 100;
    if (percentage >= 90) return "text-green-600";
    if (percentage >= 70) return "text-yellow-600";
    return "text-red-600";
  };

  const getProgressColor = (current: number, target: number) => {
    const percentage = (current / target) * 100;
    if (percentage >= 90) return "bg-green-500";
    if (percentage >= 70) return "bg-yellow-500";
    return "bg-red-500";
  };

  const averageScore = standards.length > 0 
    ? standards.reduce((sum, s) => sum + s.current_score, 0) / standards.length 
    : 0;

  const standardsAboveTarget = standards.filter(s => s.current_score >= s.target_score).length;

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg">
          <Award className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">مراقبة جودة الخدمة</h1>
          <p className="text-muted-foreground mt-1">
            معايير الجودة ومؤشرات الأداء
          </p>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">متوسط النقاط</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageScore.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">من أصل 100</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">معايير الجودة</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{standards.length}</div>
            <p className="text-xs text-muted-foreground">معيار نشط</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">فوق المستهدف</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{standardsAboveTarget}</div>
            <p className="text-xs text-muted-foreground">من {standards.length} معيار</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">القياسات الشهرية</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.length}</div>
            <p className="text-xs text-muted-foreground">قياس مسجل</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="standards" className="space-y-6">
        <TabsList>
          <TabsTrigger value="standards">معايير الجودة</TabsTrigger>
          <TabsTrigger value="metrics">القياسات</TabsTrigger>
        </TabsList>

        <TabsContent value="standards">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">معايير الجودة</h2>
            <Dialog open={isNewStandardOpen} onOpenChange={setIsNewStandardOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 ml-2" />
                  معيار جديد
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>إضافة معيار جودة جديد</DialogTitle>
                  <DialogDescription>
                    أدخل تفاصيل معيار الجودة الجديد
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">اسم المعيار *</label>
                    <Input
                      value={newStandard.name}
                      onChange={(e) => setNewStandard({ ...newStandard, name: e.target.value })}
                      placeholder="أدخل اسم المعيار"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">الفئة</label>
                    <Select value={newStandard.category} onValueChange={(value) => setNewStandard({ ...newStandard, category: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر الفئة" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer_service">خدمة العملاء</SelectItem>
                        <SelectItem value="booking_process">عملية الحجز</SelectItem>
                        <SelectItem value="communication">التواصل</SelectItem>
                        <SelectItem value="delivery">التسليم</SelectItem>
                        <SelectItem value="technical">التقنية</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">النقاط المستهدفة</label>
                    <Input
                      type="number"
                      value={newStandard.target_score}
                      onChange={(e) => setNewStandard({ ...newStandard, target_score: parseInt(e.target.value) })}
                      min="0"
                      max="100"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">الوصف</label>
                    <Textarea
                      value={newStandard.description}
                      onChange={(e) => setNewStandard({ ...newStandard, description: e.target.value })}
                      placeholder="وصف المعيار وطريقة القياس"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={() => createStandardMutation.mutate(newStandard)}
                      disabled={!newStandard.name || createStandardMutation.isPending}
                    >
                      إنشاء المعيار
                    </Button>
                    <Button variant="outline" onClick={() => setIsNewStandardOpen(false)}>
                      إلغاء
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {standards.map((standard) => (
              <Card key={standard.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{standard.name}</CardTitle>
                      <CardDescription>{standard.description}</CardDescription>
                      {standard.category && (
                        <Badge variant="outline" className="mt-2">
                          {standard.category === 'customer_service' && 'خدمة العملاء'}
                          {standard.category === 'booking_process' && 'عملية الحجز'}
                          {standard.category === 'communication' && 'التواصل'}
                          {standard.category === 'delivery' && 'التسليم'}
                          {standard.category === 'technical' && 'التقنية'}
                        </Badge>
                      )}
                    </div>
                    <div className="text-left">
                      <div className={`text-2xl font-bold ${getScoreColor(standard.current_score, standard.target_score)}`}>
                        {standard.current_score}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        الهدف: {standard.target_score}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>التقدم نحو الهدف</span>
                      <span>{Math.min(Math.round((standard.current_score / standard.target_score) * 100), 100)}%</span>
                    </div>
                    <Progress 
                      value={Math.min((standard.current_score / standard.target_score) * 100, 100)} 
                      className="h-2"
                    />
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => {
                      setSelectedStandard(standard.id);
                      setIsNewMetricOpen(true);
                    }}
                  >
                    تسجيل قياس جديد
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="metrics">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">القياسات المسجلة</h2>
          </div>

          <div className="grid gap-4">
            {metrics.map((metric) => {
              const standard = standards.find(s => s.id === metric.standard_id);
              return (
                <Card key={metric.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{standard?.name}</div>
                        <div className="text-sm text-muted-foreground">
                          الفترة: {metric.period} • النقاط: {metric.score}
                        </div>
                        {metric.notes && (
                          <div className="text-sm text-muted-foreground mt-1">
                            ملاحظات: {metric.notes}
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="text-lg font-bold">{metric.score}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(metric.recorded_at).toLocaleDateString('ar-SA')}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* New Metric Dialog */}
      <Dialog open={isNewMetricOpen} onOpenChange={setIsNewMetricOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>تسجيل قياس جديد</DialogTitle>
            <DialogDescription>
              أدخل نتيجة القياس للمعيار المحدد
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">النقاط (0-100)</label>
              <Input
                type="number"
                value={newMetric.score}
                onChange={(e) => setNewMetric({ ...newMetric, score: parseInt(e.target.value) })}
                min="0"
                max="100"
                placeholder="أدخل النقاط"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">فترة القياس</label>
              <Input
                type="month"
                value={newMetric.period}
                onChange={(e) => setNewMetric({ ...newMetric, period: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">ملاحظات</label>
              <Textarea
                value={newMetric.notes}
                onChange={(e) => setNewMetric({ ...newMetric, notes: e.target.value })}
                placeholder="أدخل أي ملاحظات أو تفاصيل إضافية"
                rows={3}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                onClick={() => createMetricMutation.mutate(newMetric)}
                disabled={!selectedStandard || createMetricMutation.isPending}
              >
                تسجيل القياس
              </Button>
              <Button variant="outline" onClick={() => setIsNewMetricOpen(false)}>
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}